"use strict";

/**
 * Toggles the visibility of a content element by expanding or collapsing it with a "show more" or "show less" button.
 *
 * @param {jQuery} $element - A jQuery object of the element to apply the show more/less functionality.
 * @param {Object} [options] - Optional settings to customize the behavior and appearance of the toggle functionality.
 * @param {number} [options.minHeight] - The minimum height of the element when collapsed. Defaults to half of the element's full height.
 * @param {string} [options.buttonTxtMore] - The text displayed on the button when the content is collapsed. Defaults to "show more".
 * @param {string} [options.buttonTxtLess] - The text displayed on the button when the content is expanded. Defaults to "show less".
 * @param {string} [options.buttonCss] - The CSS class applied to the toggle button. Defaults to "showmore-button".
 * @param {number} [options.animationSpeed] - The speed of the expand/collapse animation in milliseconds. Defaults to half of the element's full height.
 * @param {Function} [viewMoreCallBack] - Callback function to be executed when the content is expanded.
 * @param {Function} [viewLessCallBack] - Callback function to be executed when the content is collapsed.
 */
export function showMore(
    $element,
    options = {},
    viewMoreCallBack,
    viewLessCallBack,
) {
    const defaultHeight = $element.prop("scrollHeight") / 2;
    const settings = $.extend(
        {
            minHeight: defaultHeight,
            buttonTxtMore: "Show More",
            buttonTxtLess: "Show Less",
            buttonCss: "btn showmore-button",
            animationSpeed: defaultHeight,
            lineClampClassName: "line-clamp"
        },
        options,
    );

    const toggleAriaExpanded = (elements, ariaExpanded) => {
        elements.forEach((element) =>
            element.attr("aria-expanded", ariaExpanded),
        );
    };

    const getFullHeight = () => {
        const isLineClamped = $element.css("line-clamp");

        isLineClamped !== "none" &&
            $element.removeClass(settings.lineClampClassName).css("line-clamp", "");

        return $element.prop("scrollHeight");
    };

    if ($element.prop("scrollHeight") > settings.minHeight) {
        $element.css({
            "min-height": settings.minHeight,
            "max-height": settings.minHeight,
            overflow: "hidden",
            width: $element.css("width"),
        });

        if ($element.next().hasClass(settings.buttonCss)) {
            return;
        }

        const showMoreButton = $("<button />", {
            class: settings.buttonCss,
            type: "button",
            html: settings.buttonTxtMore,
            on: {
                click() {
                    const isCollapsed = $element.css("max-height") !== "none";

                    if (isCollapsed) {
                        const fullHeight = getFullHeight();

                        $element
                            .css("height", settings.minHeight)
                            .css("max-height", "")
                            .animate(
                                { height: fullHeight },
                                settings.animationSpeed,
                                function () {
                                    showMoreButton.html(settings.buttonTxtLess);
                                    toggleAriaExpanded(
                                        [showMoreButton, $element],
                                        true,
                                    );
                                    $element.css("height", "");
                                    if (viewLessCallBack) viewLessCallBack();
                                },
                            );
                    } else {
                        $element.animate(
                            { height: settings.minHeight },
                            settings.animationSpeed,
                            function () {
                                showMoreButton.html(settings.buttonTxtMore);
                                $element.css("max-height", settings.minHeight);
                                toggleAriaExpanded(
                                    [showMoreButton, $element],
                                    false,
                                );
                                if (viewMoreCallBack) viewMoreCallBack();
                            },
                        );
                    }
                },
            },
        });

        $element.after(showMoreButton);
    }
}
